import React from 'react';
import styled from 'styled-components';

import { AccordionBanner } from 'src/payments/components/accordion-banner/accordion-banner';
import {
  accordionFullWidthReset,
  HeadlineText,
} from 'src/payments/components/incentive-accordion-styles/incentive-accordion-styles';

import { PointsBoostDetails } from './points-boost-details';

type PointsBoostIncentiveBannerProps = {
  'data-testid'?: string;
  borderRadius?: string;
  className?: string;
  marginBottom?: string;
  // Display label from the points-boost foundation (e.g. "2x"); never a dollar amount.
  multiplierLabel: string;
  paymentStepText?: string;
};

// The "How it works." underline is stylistic only (owner copy decision): the accordion
// summary itself is the disclosure control, so there is deliberately no link or button here.
export const PointsBoostIncentiveBanner = ({
  'data-testid': dataTestId,
  borderRadius = '4px 4px 4px 4px',
  className,
  marginBottom,
  multiplierLabel,
  paymentStepText,
}: PointsBoostIncentiveBannerProps): JSX.Element => (
  <StyledAccordionBanner
    className={className}
    data-testid={dataTestId}
    marginBottom={marginBottom}
    borderRadius={borderRadius}
    graphic={
      <GraphicLockup data-testid='points-boost-incentive-graphic'>
        <GraphicMultiplier $charCount={multiplierLabel.length}>{multiplierLabel}</GraphicMultiplier>
        <GraphicCaption>points</GraphicCaption>
      </GraphicLockup>
    }
    mainContent={
      <HeadlineText>
        Get <strong>{multiplierLabel} rewards</strong> on all orders paid by bank! <u>How it works.</u>
      </HeadlineText>
    }
    toggleContent={<PointsBoostDetails multiplierLabel={multiplierLabel} paymentStepText={paymentStepText} />}
  />
);

const StyledAccordionBanner = styled(AccordionBanner)`
  &.MuiPaper-root {
    ${accordionFullWidthReset}
  }

  & > .MuiCollapse-container {
    border-top: 1px solid var(--dcm-pdp-incentive-border-color, #8fcfc0);
  }
`;

const GraphicLockup = styled.span`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 48px;
`;

// ~0.6em average glyph width at 700 weight: 80/charCount keeps the label inside the 48px lane
// ("2x" -> 32px, "10x" -> 26px, "2.5x" -> 20px, "2.33x" -> 16px).
const multiplierFontSize = (charCount: number): number => Math.max(16, Math.min(32, Math.floor(80 / charCount)));
const multiplierLineHeight = (charCount: number): number => Math.max(12, Math.min(24, Math.floor(80 / charCount)));

const GraphicMultiplier = styled.span<{ $charCount: number }>`
  -webkit-text-stroke: 0.2px var(--dcm-pdp-incentive-graphic-stroke-color, #002101);
  color: var(--dcm-pdp-incentive-accent, #00a47c);
  font-size: ${({ $charCount }) => multiplierFontSize($charCount)}px;
  font-weight: 700;
  line-height: ${({ $charCount }) => multiplierLineHeight($charCount)}px;
`;

const GraphicCaption = styled.span`
  -webkit-text-stroke: 0.2px var(--dcm-pdp-incentive-graphic-stroke-color, #002101);
  color: var(--dcm-pdp-incentive-accent, #00a47c);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;
